import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Forum as ForumIcon,
  Announcement as AnnouncementIcon,
  Route as RouteIcon,
  EventSeat as EventSeatIcon,
  DirectionsBus as DirectionsBusIcon,
  Place as PlaceIcon,
  DriveEta as DriveEtaIcon,
  Build as BuildIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Assessment as AssessmentIcon,
  Lightbulb as LightbulbIcon,
  LocationCity as LocationCityIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 240;

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
}

const menuItems: MenuItem[] = [
  { text: '대시보드', icon: <DashboardIcon />, path: '/dashboard' },
  { text: '민원관리', icon: <ForumIcon />, path: '/complaints' },
  { text: '제안관리', icon: <LightbulbIcon />, path: '/suggestions', superAdminOnly: true },
  { text: '공지사항', icon: <AnnouncementIcon />, path: '/notices', superAdminOnly: true },
  { text: '첨부파일 관리', icon: <AttachFileIcon />, path: '/attachments', superAdminOnly: true },
  { text: '노선 정보', icon: <RouteIcon />, path: '/routes' },
  { text: '승차 정보', icon: <EventSeatIcon />, path: '/boarding', superAdminOnly: true },
  { text: '운행기록', icon: <DirectionsBusIcon />, path: '/operation' },
  { text: '정류장별 운행기록', icon: <PlaceIcon />, path: '/operation/stations', superAdminOnly: true },
  { text: '운전자 관리', icon: <DriveEtaIcon />, path: '/driver' },
  { text: '점검기록', icon: <BuildIcon />, path: '/maintenance' },
  { text: '관리자 관리', icon: <AdminPanelSettingsIcon />, path: '/admin', adminOnly: true },
  { text: '보고서 관리', icon: <AssessmentIcon />, path: '/reports', superAdminOnly: true },
  { text: '거주지 통계', icon: <LocationCityIcon />, path: '/residence-stats', superAdminOnly: true },
];

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminType } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.superAdminOnly && adminType !== 'super') {
      return false;
    }
    if (item.adminOnly && adminType !== 'super') {
      return false;
    }
    return true;
  });

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DirectionsBusIcon sx={{ color: '#60a5fa' }} />
          <Box>
            <Box sx={{ fontSize: '0.875rem', fontWeight: 600 }}>부산항 신항</Box>
            <Box sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>셔틀버스 관리자</Box>
          </Box>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: '#334155' }} />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleMenuItemClick(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: '#334155',
                  '&:hover': {
                    backgroundColor: '#475569',
                  },
                },
                '&:hover': {
                  backgroundColor: '#2d3748',
                },
              }}
            >
              <ListItemIcon sx={{ color: '#94a3b8', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  return (
    <Box component="nav">
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#1e293b',
            color: '#fff',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#1e293b',
            color: '#fff',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};
